/**
 * Product Customizations - Moon Moon Agency
 *
 * Personnalisation de la fiche produit (monogramme, typographie, gravure...).
 * Figma : 2818:140405 / 2818:157318 — « Option selection/Open/Perso ».
 *
 * Interaction en deux niveaux : le client choisit un GROUPE (ou « Pas de
 * personnalisation »), ce qui revele le panneau du groupe ; il y choisit ensuite
 * une OPTION, puis saisit son texte et sa couleur. Le Liquid rend tous les
 * panneaux ; ce module gere la selection et prepare l'ajout au panier.
 *
 * Ajout au panier : deux lignes, le produit principal et le variant add-on
 * facture a part, liees par la convention `_mm_bundle_*` partagee avec le
 * configurateur (cf. mm_cart_grouping.js) — le panier les affiche regroupees.
 * L'interception du submit vit ici et non dans mm_cart.js : sans produit
 * personnalisable sur la page, le comportement du panier reste inchange.
 *
 * Plusieurs instances par page : la fiche produit en a une, une grille
 * collection peut en avoir une par carte a variantes/perso. Tout est donc en
 * delegation document-wide, la racine `[data-product-customizations]` et le
 * form etant resolus a chaque evenement plutot que memorises une fois.
 */

const ProductCustomizationsManager = {
	config: {
		selectors: {
			root: '[data-product-customizations]',
			none: '[data-customization-none]',
			group: '[data-customization-group]',
			panel: '[data-customization-panel]',
			option: '[data-customization-option]',
			input: '[data-customization-input]',
			image: '[data-customization-image]',
			color: '[data-customization-color]',
			error: '[data-customization-error]',
			cardForm: '[data-type="add-to-cart-form"]',
		},
	},

	state: { bound: false, imageCache: {} },

	init() {
		if (this.state.bound || !document.querySelector(this.config.selectors.root)) return;
		this.state.bound = true;

		document.addEventListener('click', (event) => {
			const none = event.target.closest(this.config.selectors.none);
			if (none) { this._selectGroup(none.closest(this.config.selectors.root), null); return; }

			// Second clic sur le groupe actif : il se referme (seul moyen de retirer la
			// personnalisation quand le produit masque « Pas de personnalisation »).
			const group = event.target.closest(this.config.selectors.group);
			if (group) {
				const isOpen = group.getAttribute('aria-pressed') === 'true';
				this._selectGroup(group.closest(this.config.selectors.root), isOpen ? null : group);
				return;
			}

			const option = event.target.closest(this.config.selectors.option);
			if (option) { this._selectOption(option); return; }

			const color = event.target.closest(this.config.selectors.color);
			if (color) this._selectColor(color);
		});

		document.addEventListener('input', (event) => {
			const input = event.target.closest(this.config.selectors.input);
			if (input) this._clearError(input.closest(this.config.selectors.root));
		});

		// Emis par la fiche produit et les cartes une fois le variant id du form a jour.
		document.addEventListener('product:variant-input-updated', (event) => {
			this._syncVariant(event.detail.form, event.detail.variantId);
		});

		this._bindSubmit();
	},

	/**
	 * Variante exclue (metafield custom.noCustomization) : le bloc est masque et
	 * la selection videe, pour ne pas ajouter un add-on que le client ne voit
	 * plus. Au retour sur une variante personnalisable, on retrouve l'etat du
	 * chargement : « Pas de personnalisation » s'il existe, sinon le 1er groupe.
	 */
	_syncVariant(form, variantId) {
		const root = form?.querySelector(this.config.selectors.root);
		if (!root) return;

		this._syncVariantImage(root, variantId);
		if (!root.dataset.excludedVariants) return;

		const excluded = root.dataset.excludedVariants.split(',').includes(String(variantId));
		if (excluded === root.hidden) return;

		const reopen = excluded || root.querySelector(this.config.selectors.none)
			? null
			: root.querySelector(this.config.selectors.group);
		this._selectGroup(root, reopen);
		root.hidden = excluded;
	},

	/**
	 * Vignette sans visuel d'option (data-variant-image) : elle reprend l'image de
	 * la variante, fournie par la table data-variant-images du Liquid.
	 */
	_syncVariantImage(root, variantId) {
		let images;
		try {
			images = JSON.parse(root.dataset.variantImages || '{}');
		} catch (error) {
			return;
		}
		const src = images[String(variantId)];
		if (!src) return;

		root.querySelectorAll(this.config.selectors.image + '[data-variant-image]').forEach((img) => {
			img.src = src;
			img.dataset.baseSrc = src;
		});
	},

	/**
	 * Intercepte le submit du formulaire produit uniquement quand une
	 * personnalisation est active sur SON bloc : sinon le flux natif
	 * (mm_cart.js) s'applique. En capture, pour passer avant le handler
	 * generique du panier.
	 */
	_bindSubmit() {
		document.addEventListener('submit', (event) => {
			const form = event.target;
			if (!form.matches(this.config.selectors.cardForm)) return;

			// Bloc masque (variante sans personnalisation) : flux panier natif.
			const root = form.querySelector(this.config.selectors.root);
			if (!root || root.hidden) return;

			const selection = this._selection(root);
			if (!selection) return;

			event.preventDefault();
			event.stopImmediatePropagation();

			if (selection.error) {
				this._showError(root, selection.error);
				selection.input?.focus();
				return;
			}

			this._addToCart(form, root, selection);
		}, true);
	},

	/**
	 * Niveau 1 : un seul groupe ouvert a la fois. `null` referme tout et remet
	 * le produit a nu.
	 */
	_selectGroup(root, groupButton) {
		if (!root) return;

		this._all(root, this.config.selectors.group).forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
		this._all(root, this.config.selectors.panel).forEach((panel) => { panel.hidden = true; });
		root.querySelector(this.config.selectors.none)?.setAttribute('aria-pressed', groupButton ? 'false' : 'true');
		this._clearError(root);

		// Repartir d'une option vierge : garder la selection precedente d'un groupe
		// referme ferait ajouter au panier une option que le client ne voit plus.
		this._all(root, this.config.selectors.option).forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
		// La saisie n'apparait qu'au choix d'une option Texte.
		this._all(root, this.config.selectors.input).forEach((input) => {
			input.hidden = true;
			if (input.dataset.groupMaxLength) input.setAttribute('maxlength', input.dataset.groupMaxLength);
		});

		if (!groupButton) return;

		groupButton.setAttribute('aria-pressed', 'true');
		const panel = this._panelFor(root, groupButton);
		if (panel) panel.hidden = false;
	},

	/**
	 * Niveau 2 : une seule option active dans le panneau. Un second clic la
	 * retire — la personnalisation reste facultative une fois le groupe ouvert.
	 */
	_selectOption(option) {
		const panel = option.closest(this.config.selectors.panel);
		if (!panel) return;

		const wasActive = option.getAttribute('aria-pressed') === 'true';
		panel.querySelectorAll(this.config.selectors.option).forEach((btn) => btn.setAttribute('aria-pressed', 'false'));
		this._clearError(option.closest(this.config.selectors.root));

		const input = panel.querySelector(this.config.selectors.input);

		if (wasActive) {
			// Sans option, la saisie reprend la limite la plus large du groupe.
			if (input) {
				input.hidden = true;
				if (input.dataset.groupMaxLength) input.setAttribute('maxlength', input.dataset.groupMaxLength);
			}
			return;
		}

		option.setAttribute('aria-pressed', 'true');

		// Le champ n'apparait que pour une option de type Texte, et adopte la limite
		// de caracteres de cette option — les options d'un groupe se distinguent
		// justement par cette longueur (« 10 caracteres » vs « 18 caracteres »).
		if (input) {
			const requiresText = option.hasAttribute('data-requires-text');
			input.hidden = !requiresText;
			if (requiresText) {
				const max = option.dataset.maxLength;
				if (max) {
					input.setAttribute('maxlength', max);
					// Une saisie plus longue devient invalide en changeant d'option.
					if (input.value.length > Number(max)) input.value = input.value.slice(0, Number(max));
				} else {
					input.removeAttribute('maxlength');
				}
				input.focus();
			}
		}

		// Le visuel du bloc OPTION suit l'option choisie, puis la couleur active.
		const image = panel.querySelector(this.config.selectors.image);
		if (image && option.dataset.optionImage) {
			image.dataset.baseSrc = option.dataset.optionImage;
			image.src = option.dataset.optionImage;
			const activeColor = panel.querySelector('button' + this.config.selectors.color + '[aria-pressed="true"]');
			if (activeColor) this._swapImage(panel, activeColor);
		}
	},

	_selectColor(button) {
		const panel = button.closest(this.config.selectors.panel);
		if (!panel) return;

		panel.querySelectorAll('button' + this.config.selectors.color).forEach((btn) => {
			btn.setAttribute('aria-pressed', 'false');
		});
		button.setAttribute('aria-pressed', 'true');

		const code = button.dataset.colorCode;
		if (code) panel.style.setProperty('--mm-customization-color', code);

		this._swapImage(panel, button);
	},

	/**
	 * Visuel colore : fichier distinct nomme `<base>-<couleur>.<ext>`, qui
	 * n'existe pas pour toutes les combinaisons. On ne remplace l'image qu'apres
	 * confirmation qu'elle se charge, sinon on garde l'image de base — un 404
	 * laisserait une image cassee.
	 */
	_swapImage(panel, colorButton) {
		const image = panel.querySelector(this.config.selectors.image);
		if (!image) return;

		const baseSrc = image.dataset.baseSrc;
		const coloredSrc = this._coloredImageUrl(baseSrc, colorButton.dataset.customizationColor || '');

		if (!coloredSrc) { image.src = baseSrc; return; }

		const cached = this.state.imageCache[coloredSrc];
		if (cached === true) { image.src = coloredSrc; return; }
		if (cached === false) { image.src = baseSrc; return; }

		const probe = new Image();
		probe.onload = () => {
			this.state.imageCache[coloredSrc] = true;
			// L'utilisateur a pu changer de couleur ou d'option entre-temps : ne rien
			// ecraser si ce bouton n'est plus actif ou si l'image de base a change.
			if (colorButton.getAttribute('aria-pressed') === 'true' && image.dataset.baseSrc === baseSrc) {
				image.src = coloredSrc;
			}
		};
		probe.onerror = () => {
			this.state.imageCache[coloredSrc] = false;
			if (colorButton.getAttribute('aria-pressed') === 'true' && image.dataset.baseSrc === baseSrc) {
				image.src = baseSrc;
			}
		};
		probe.src = coloredSrc;
	},

	/**
	 * `.../monogramme-1.jpg?width=260` + "Bleu nuit" → `.../monogramme-1-bleu-nuit.jpg?width=260`.
	 * Les parametres de l'URL CDN sont preserves (width, version).
	 */
	_coloredImageUrl(baseSrc, colorName) {
		if (!baseSrc || !colorName) return '';

		const slug = colorName
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '')
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-+|-+$/g, '');
		if (!slug) return '';

		const [path, query] = baseSrc.split('?');
		const dot = path.lastIndexOf('.');
		if (dot < 0) return '';

		const colored = path.slice(0, dot) + '-' + slug + path.slice(dot);
		return query ? colored + '?' + query : colored;
	},

	/**
	 * Etat courant de la personnalisation d'un bloc donne.
	 * @returns {object|null} null si aucune option active (flux panier inchange)
	 */
	_selection(root) {
		const option = root.querySelector(this.config.selectors.option + '[aria-pressed="true"]');
		if (!option) return null;

		const panel = option.closest(this.config.selectors.panel);
		const input = panel?.querySelector(this.config.selectors.input);
		const value = input && !input.hidden ? input.value.trim() : '';

		if (option.hasAttribute('data-requires-text') && !value) {
			return { error: root.dataset.errorEmpty, input };
		}

		// Couleur uniquement quand le client a eu le choix (groupe a 2+ couleurs) :
		// une couleur unique n'est pas un choix, elle ne part pas au panier.
		const colorEl = panel?.querySelector('button' + this.config.selectors.color + '[aria-pressed="true"]');

		return {
			variantId: option.dataset.variantId,
			label: option.dataset.label || '',
			groupLabel: option.dataset.groupLabel || '',
			value: value || option.dataset.label || '',
			color: colorEl?.dataset.customizationColor || '',
			input,
		};
	},

	async _addToCart(form, root, selection) {
		const formData = new FormData(form);
		const mainVariantId = formData.get('id');
		const quantity = parseInt(formData.get('quantity') || 1, 10);
		const submitButtons = Array.from(form.querySelectorAll('[type="submit"]'));

		// Identifiant de groupe : relie les deux lignes entre elles pour le
		// regroupement visuel et la suppression en cascade.
		const groupId = this._groupId();

		// Les libelles des proprietes sont visibles dans le panier et sur la commande :
		// ils viennent des locales via data-* sur le conteneur, pas d'une chaine en dur.
		const labels = root.dataset;
		const sharedProperties = { _mm_bundle_group: groupId };
		if (selection.value) sharedProperties[labels.labelText || 'Personnalisation'] = selection.value;
		if (selection.color) sharedProperties[labels.labelColor || 'Couleur'] = selection.color;

		// Libelle affiche de la sous-ligne panier ("Personnalisation Monogramme
		// une lettre") : le groupe et l'option sont dynamiques, le gabarit vient
		// des locales (placeholders [group]/[option], meme pattern que [montant]
		// dans MM-Cart.liquid). [option] n'est jamais en tete de phrase dans ces
		// gabarits : sa capitale de metaobjet ("Une lettre") est mise en minuscule
		// pour lire une phrase normale plutot qu'un doublon de majuscule.
		const optionLabel = selection.label.charAt(0).toLowerCase() + selection.label.slice(1);
		const addonLabel = (labels.labelAddon || '[group] [option]')
			.replace('[group]', selection.groupLabel)
			.replace('[option]', optionLabel)
			.replace(/\s+/g, ' ')
			.trim();

		const items = [
			{
				id: mainVariantId,
				quantity,
				properties: { ...sharedProperties, _mm_bundle_parent: groupId },
			},
			{
				// Une personnalisation par exemplaire commande : la quantite de
				// l'add-on suit celle du produit principal.
				id: selection.variantId,
				quantity,
				properties: {
					...sharedProperties,
					_mm_bundle_child: groupId,
					_mm_addon_label: addonLabel,
				},
			},
		];

		const sectionId = this._cartSectionId();
		const body = { items };
		if (sectionId) body.sections = sectionId;

		submitButtons.forEach((btn) => {
			btn.disabled = true;
			btn.setAttribute('aria-busy', 'true');
			btn.classList.add('mm-loading');
		});

		// shopify:cart:lines-update au DEBUT de l'ajout (spec), promise reglee sur tous les chemins
		const standardEvent = window.mmStandardEvents?.linesUpdate(root, {
			action: 'add',
			context: 'product',
			lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
		});

		try {
			// mm_cart.js est charge a la demande (PERF-15) : l'attendre avant CartAPI.
			await window.mmLoadCart?.();
			// CartAPI.request rafraichit le drawer et emet les evenements panier.
			const data = await window.CartAPI.request('POST', 'cart/add.js', body);
			// cart/add.js ne renvoie que les lignes ajoutees : le panier complet est relu
			standardEvent?.resolve();
			this._clearError(root);

			// Evenement interne, meme nom que le flux natif (mm_cart.js)
			window.emit('cart:add-to-cart-success', { response: data, items });
			if (typeof window.mmOpenCart === 'function') window.mmOpenCart();
		} catch (error) {
			standardEvent?.fail(error);
			this._showError(root, error.message);
			window.log('error', '🎨 [CUSTOMIZATIONS]', 'Add to cart failed', { error: error.message });
		} finally {
			// Filet : la promise de l'event standard doit toujours etre reglee
			if (standardEvent && !standardEvent.settled) standardEvent.resolve();
			submitButtons.forEach((btn) => {
				btn.disabled = false;
				btn.removeAttribute('aria-busy');
				btn.classList.remove('mm-loading');
			});
		}
	},

	_groupId() {
		if (window.crypto && typeof window.crypto.randomUUID === 'function') {
			return window.crypto.randomUUID();
		}
		return 'mmc-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
	},

	_cartSectionId() {
		const drawer = document.querySelector('[data-drawer-cart]');
		const section = drawer && drawer.closest('[id^="shopify-section-"]');
		return section ? section.id.replace('shopify-section-', '') : '';
	},

	_panelFor(root, groupButton) {
		const handle = groupButton.dataset.customizationGroup;
		return root.querySelector(`[data-customization-panel="${handle}"]`);
	},

	_all(root, selector) {
		return Array.from(root.querySelectorAll(selector) || []);
	},

	_showError(root, message) {
		const el = root?.querySelector(this.config.selectors.error);
		if (!el || !message) return;
		el.textContent = message;
		el.hidden = false;
	},

	_clearError(root) {
		const el = root?.querySelector(this.config.selectors.error);
		if (!el) return;
		el.hidden = true;
		el.textContent = '';
	},
};

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => ProductCustomizationsManager.init());
} else {
	ProductCustomizationsManager.init();
}
document.addEventListener('shopify:section:load', () => ProductCustomizationsManager.init());
